import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dropdown, Avatar, Button, Input } from "antd";
import {
  CheckOutlined,
  PlusOutlined,
  CloseCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import AddNewFooter from "../SelectAddNew";
import UserAvatar from "../UserAvatar";
import "./index.css";

const toOption = (item, options) => {
  if (item == null) return null;
  if (typeof item === "object")
    return { label: item.label, value: item.value, avatar: item.avatar };
  const found = options.find((o) => o.value === item);
  return found || { label: String(item), value: item };
};

const avatarSrc = (opt) =>
  typeof opt?.avatar === "string" ? opt.avatar : opt?.avatar?.original_url;

/* Picker options carry a display label and an id, not the two name fields, so
 * they are adapted to what UserAvatar reads. The id is the seed for the
 * identity colour, which keeps a person the same colour here as in the roster
 * and on their task cards. */
const asUser = (opt) => ({
  uuid: String(opt?.value ?? ""),
  name: opt?.label,
  avatar: avatarSrc(opt) ? { original_url: avatarSrc(opt) } : null,
});

const AvatarSelect = ({
  options = [],
  value,
  onChange,
  onClear,
  onSearch,
  showSearch = false,
  allowClear = false,
  mode,
  bordered = false,
  placeholder = "انتخاب کنید",
  maxCount = 3,
  size = "middle",
  disabled = false,
  style,
  className,
  // eslint-disable-next-line no-unused-vars
  filterOption,
  addNew = false,
  /* «افزودن مورد جدید» تا حالا همیشه «کاربر جدید» بود، پس این پیکر فقط برای
     آدم‌ها قابل استفاده بود. حالا مقصدش پارامتر است و مشتری هم می‌تواند از همین
     کامپوننت استفاده کند. */
  addNewProps = {
    label: "کاربر جدید",
    to: "/users?action=new",
    permission: "manage_users",
  },
  ...props
}) => {
  const isMultiple = mode === "multiple";
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const selectedOptions = useMemo(() => {
    if (value == null || value === "") return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr.map((v) => toOption(v, options)).filter(Boolean);
  }, [value, options]);

  const selectedIds = selectedOptions.map((o) => o.value);

  const visibleOptions = useMemo(() => {
    if (onSearch || !search) return options;
    return options.filter((o) =>
      String(o.label ?? "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [options, search, onSearch]);

  const emit = (ids, opts) => {
    if (isMultiple) {
      onChange?.(ids, opts);
    } else {
      onChange?.(ids[0], opts[0] ?? null);
    }
  };

  const handleToggle = (opt) => {
    if (isMultiple) {
      const exists = selectedIds.includes(opt.value);
      const nextOpts = exists
        ? selectedOptions.filter((o) => o.value !== opt.value)
        : [...selectedOptions, opt];
      emit(
        nextOpts.map((o) => o.value),
        nextOpts
      );
    } else {
      emit([opt.value], [opt]);
      setOpen(false);
    }
  };

  const handleClear = (e) => {
    e?.stopPropagation();
    onClear?.();
    if (isMultiple) onChange?.([], []);
    else onChange?.(undefined, null);
  };

  const handleSearch = (val) => {
    setSearch(val);
    onSearch?.(val);
  };

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const dropdownContent = (
    <div className="custom-dropdown" ref={dropdownRef}>
      {showSearch && (
        <div className="custom-dropdown-search">
          <Input
            autoFocus
            allowClear
            size="small"
            prefix={<SearchOutlined style={{ color: "var(--ds-placeholder)" }} />}
            placeholder="جست و جو"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      )}
      {visibleOptions?.length > 0 ? (
        <ul>
          {visibleOptions.map((opt) => {
            const selected = selectedIds.includes(opt.value);
            return (
              <li
                key={opt.value}
                tabIndex={0}
                onClick={() => handleToggle(opt)}
                className={selected ? "selected" : ""}
              >
                <UserAvatar user={asUser(opt)} size={24} />
                <span className="custom-dropdown-label">{opt.label}</span>
                {selected && <CheckOutlined style={{ marginRight: "auto" }} />}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="placeholder">موردی یافت نشد</div>
      )}
      {addNew && <AddNewFooter {...addNewProps} />}
    </div>
  );

  const hasValue = selectedOptions.length > 0;
  const showClear = allowClear && hasValue && !disabled;

  return (
    <Dropdown
      open={open}
      disabled={disabled}
      trigger={["click"]}
      onOpenChange={setOpen}
      dropdownRender={() => dropdownContent}
    >
      <div
        className={`avatar-select-trigger avatar-select-${size} ${
          bordered ? "avatar-select-bordered" : ""
        } ${disabled ? "avatar-select-disabled" : ""} ${className || ""}`}
        style={style}
        {...props}
      >
        <Button
          icon={<PlusOutlined />}
          type="default"
          shape="circle"
          size={size === "large" ? "middle" : "small"}
          disabled={disabled}
        />
        {hasValue ? (
          isMultiple ? (
            <Avatar.Group
              maxCount={maxCount}
              maxStyle={{ color: "#f56a00", backgroundColor: "#fde3cf" }}
            >
              {selectedOptions.map((opt) => (
                <UserAvatar key={opt.value} user={asUser(opt)} size={32} />
              ))}
            </Avatar.Group>
          ) : (
            <div className="avatar-select-single">
              <UserAvatar user={asUser(selectedOptions[0])} size={24} />
              <span className="avatar-select-single-label">
                {selectedOptions[0]?.label}
              </span>
            </div>
          )
        ) : (
          <span className="avatar-select-placeholder">{placeholder}</span>
        )}
        {showClear && (
          <CloseCircleFilled
            className="avatar-select-clear"
            onClick={handleClear}
          />
        )}
      </div>
    </Dropdown>
  );
};

export default AvatarSelect;
